import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { getVendorInfolist, resendVendorInvite } from "../../api/BorrowerApi";
import { getMockVendorInfolist, resendMockVendorInvite } from "../../api/mock/MockBorrowerApi";
import { formatDataProperties, guid, handleErrorResponse } from "../../Helpers";
import { IVendorObj } from "../interfaces/Borrower";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BCButton from "../ui/BCButton";
import BCMenuBar from "../ui/BCMenuBar";
import BCMessage from "../ui/BCMessage";
import BCTable from "../ui/BCTable";
import BWExportButton from "./BWExportButton";
import BWSearchBox from "./BWSearchBox";
import "../../styles/borrower/vendor-form.scss";
import BWActionsButton from "./BWActionsButton";
import BCFormInput from "../ui/BCFormInput";
import { GlobalConstants } from "../constants/GlobalConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";
import BCConfirmationModal from "../ui/BCConfirmationModal";
import { handleApiPromise } from "../../helpers/GlobalHelper";

interface IBWVendor {
  isGreaterThanMobile: boolean;
  isGreaterThanTablet: boolean;
  isSidebar: boolean;
  isMessage: boolean;
  setIsMessage: React.Dispatch<React.SetStateAction<boolean>>;
  errorResponse: IErrorResponse | undefined;
  setErrorResponse: React.Dispatch<React.SetStateAction<IErrorResponse | undefined>>
  proxyUser: IProxyUser | undefined;
}

const BWVendor = ({ ...props }: IBWVendor) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [isResendInviteModalOpen, setIsResendInviteModalOpen] = useState(false as boolean);
  const [vendorInfoData, setVendorInfoData] = useState([] as IVendorObj[]);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false as boolean);
  const [selectedVendors, setSelectedVendors] = useState([] as any[]);

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleToVendorForm = () => {
    history.push(`/vendor/${guid()}`);
  };

  const handleSelectVendor = (vendorId) => {
    const selectedVendor = vendorInfoData.find(vendor => vendor.id === vendorId);
    if (selectedVendor) {
      selectedVendor.selected = !selectedVendor.selected;
    }

    if (!selectedVendors.find(x => x === vendorId)) {
      setSelectedVendors([...selectedVendors, vendorId]);
    } else {
      setSelectedVendors(selectedVendors.filter(x => x !== vendorId));
    }
  };

  const formatVendorList = (vendors) => {
    const result = [] as any[];
    vendors.forEach(vendor => {
      const checkbox = <BCFormInput 
        inputKey={`vendor-${vendor.id}`}
        labelClass="borrower-table-checkbox"
        value={vendor.selected}
        type={GlobalConstants.FormInputTypes.CHECKBOX}
        handleFormChange={() => handleSelectVendor(vendor.id)}
      />;

      result.push({"action": checkbox, ...vendor});
    });

    return formatDataProperties(result, [
      {field: "borrowerOrganization", action: "rename", name: "Borrower"},
      {field: "action", action: "rename", name: ""},
    ]);
  };

  const handleResendVendorInvite = async () => {
    const inviteObj = {
      "borrowerOrganizationId": vendorInfoData.length ? vendorInfoData[0].borrowerOrganizationId : "",
      "borrowerVendorIds": selectedVendors
    };

    setIsLoading(true);
    await resendVendorInviteAsync(inviteObj);
    setIsLoading(false);
  };

  const resendVendorInviteAsync = async (inviteObj) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await resendMockVendorInvite(inviteObj) : await resendVendorInvite(inviteObj, props.proxyUser && props.proxyUser.id);
      if (response && response.ok) {
        props.setIsMessage(false);
        return true;
      }
      else {
        await handleErrorResponse(response, props.setIsMessage, props.setErrorResponse);
      }
    }
    catch (error) {
      props.setIsMessage(false);
    }
  };

  useEffect(() => {
    const getVendorInfoAysnc = async () => {
      const data = await handleApiPromise(
        () => getVendorInfolist(props.proxyUser && props.proxyUser.id),
        () => getMockVendorInfolist(),
        (response) => handleErrorResponse(response, props.setIsMessage, props.setErrorResponse)
      ) || [];

      const vendorViews = [] as IVendorObj[];
      data.forEach((vendorInfolistItem) => {
        vendorViews.push(vendorInfolistItem as IVendorObj);
      });

      setVendorInfoData(vendorViews);
      setIsLoading(false);
    };

    if (isFirstLoad) {
      getVendorInfoAysnc();
    }
  }, [props.setIsMessage, props.setErrorResponse, setIsLoading, handleError, props.proxyUser, isFirstLoad]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  return (
    <div className={(props.isGreaterThanTablet && props.isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {isLoading ? <Loader active inline="centered" size="big" /> : <React.Fragment>
        <BCConfirmationModal
          isModalOpen={isResendInviteModalOpen}
          setIsModalOpen={setIsResendInviteModalOpen}
          modalHeader="Resend Invite?"
          modalContent="Are you sure you want to send email to selected vendor(s)?"
          handleConfirm={() => handleResendVendorInvite()}
        />
        <BCConfirmationModal 
          isModalOpen={isChangeEmailModalOpen}
          setIsModalOpen={setIsChangeEmailModalOpen}
          modalHeader="Change Email?"
          modalContent="Are you sure you want to change email of selected vendor?"
          handleConfirm={() => console.log("Resending Invite to vendor")}
        />
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={props.isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => props.setIsMessage(false)}
          errorResponse={props.errorResponse}
        />
        <BCMenuBar
          isGreaterThanMobile={props.isGreaterThanMobile}
          leftChildren={[
            <BWActionsButton addOptionAction={handleToVendorForm} />,
            <BWExportButton />,
            <BCButton handleOnClick={() => setIsResendInviteModalOpen(true)} disabled={selectedVendors.length < 1} >Resend Invite</BCButton>,
             <BCButton handleOnClick={() => setIsChangeEmailModalOpen(true)} disabled={selectedVendors.length !== 1}>Change Email</BCButton>,
          ]}
          rightChildren={[<BWSearchBox />]}
        />
        <BCTable
          dataRules={[
            { field: "Id", rule: "hide" },
            { field: "Selected", rule: "hide" },
            { field: "Borrower Organization Id", rule: "hide" },
            { field: "Allow Send Invite Id", rule: "hide" },
            { field: "Has User Logged In Id", rule: "hide" },
          ]}
          data={formatVendorList(vendorInfoData)}
          selectable
          overflowX
          dataSingleLine
          sortable={["Borrower", "Name", "Address", "City", "State"]}
          pagination={25}
        />
      </React.Fragment>}
      {FeatureFlags.IS_DEBUG && <React.Fragment>
        <button onClick={() => console.log(vendorInfoData)}>vendorInfoData</button>
        <button onClick={() => console.log(selectedVendors)}>selectedVendors</button>
      </React.Fragment>}
    </div>
  );
};

export default BWVendor;